import { useSplitTreatments } from "@splitsoftware/splitio-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/use-auth";
import { cn } from "@/lib/utils";
import { BannerIcon, CloseIcon } from "./icons";
import ImageModal from "./image-modal";
import useWrapped from "./use-wrapped";

const STORAGE_KEY = "ImmersiveRecap2025";

interface ImmersiveRecap2023Props {
  className?: string;
  onClose?(): void;
  withCloseButton?: boolean;
}

export default function ImmersiveRecap2023({
  className,
  onClose = () => {
    /* No-op */
  },
  withCloseButton = false,
}: ImmersiveRecap2023Props) {
  const [isClosed, setIsClosed] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isUser, me, queryContext } = useAuth();
  const { treatments } = useSplitTreatments({ names: ["wrapped_2025"] });

  const skip =
    !isUser ||
    treatments.wrapped_2025?.treatment !== "on" ||
    (withCloseButton && isClosed);

  const { data } = useWrapped({
    skip,
    ...queryContext(),
  });

  const handleClick = () => setIsModalOpen(true);

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsClosed(true);
    localStorage.setItem(STORAGE_KEY, new Date().toString());
    onClose();
  };

  useEffect(() => {
    setIsClosed(!!localStorage.getItem(STORAGE_KEY));
  }, []);

  if (skip) {
    return null;
  }
  if (!me) {
    return null;
  }
  if (!data?.wrapped) {
    return null;
  }
  if (!(data.wrapped.escapeRoomsDone || data.wrapped.hauntsDone)) {
    return null;
  }
  if (!data.wrapped.citiesVisited) {
    return null;
  }
  if (!data.wrapped.hoursSpent) {
    return null;
  }

  return (
    <div className={cn("md:hidden", className)}>
      {/** biome-ignore lint/a11y/noStaticElementInteractions: its okay */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: its okay */}
      {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: dunno legacy */}
      <div className="relative cursor-pointer" onClick={handleClick}>
        <BannerIcon className="aspect-343/122 h-auto w-full" />
        {withCloseButton && (
          <Button
            aria-label="Close"
            className="absolute top-[7px] right-[7px] flex size-[17px] items-center justify-center"
            onClick={handleClose}
          >
            <CloseIcon />
          </Button>
        )}
      </div>
      <ImageModal
        onOpenChange={setIsModalOpen}
        open={isModalOpen}
        user={me}
        wrapped={data.wrapped}
      />
    </div>
  );
}
